import {useQueries, useQuery, useQueryClient} from '@tanstack/react-query';
import {HTTPError} from 'ky';
import {useEffect, useRef, useState} from 'react';

import {
  getAllModelPipelines,
  getCmImageView,
  getCmImageViewDistribution,
  getDatasetFinetuneDataV2,
  getEvaluationPipeline,
  getEvaluationPipelineResults,
  getEvaluationPipelines,
  getEvaluationPipelinesByModel,
  getFinetuneStatus,
  getKeypointFinetuneData,
  getKeypointFinetuneDataByDataset,
  getLatestEvaluationPipelineByTrainingPipeline,
  getLatestImageCleanupPipeline,
  getLatestImageSelectionPipeline,
  getLatestModelPipeline,
  getLatestPublishingPipeline,
  getLatestSavedFinetune,
  getLatestSuccessfulModelPipeline,
  getLatestSuccessfulTrainingGroups,
  getLatestSyntheticDefectPipeline,
  getLatestTrainingGroups,
  getPublishingPipelineStatus,
} from '~api/automl';
import {modelIdQueryParam, useQueryParam} from '~utils/routeUtil';
import {getHyperparamAwareJobStatus} from '~utils/trainingUtils';
import {JobStates} from '~redux/types/automl';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {decreaseGlobalLoadingIndicator, increaseGlobalLoadingIndicator} from '~redux/actions/globalActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

export const AutoMlQueryKeys = {
  all: ['automl'],
  allPipelines: (projectId: string, modelId?: string) => [...AutoMlQueryKeys.all, 'allPipelines', projectId, modelId],
  latestPipeline: (projectId: string, modelId?: string) => [
    ...AutoMlQueryKeys.all,
    'latestPipeline',
    projectId,
    modelId,
  ],
  latestSuccessfulPipeline: (projectId: string, modelId?: string) => [
    ...AutoMlQueryKeys.all,
    'latestSuccessfulPipeline',
    projectId,
    modelId,
  ],
  finetuneData: (projectId: string, pipelineId?: string) => [
    ...AutoMlQueryKeys.all,
    'finetuneData',
    projectId,
    pipelineId,
  ],
  datasetFinetuneData: (
    projectId: string,
    pipelineId?: string,
    datasetId?: Dataset,
    interpretation?: Interpretation,
  ) => [...AutoMlQueryKeys.all, 'datasetFinetuneData', projectId, pipelineId, datasetId, interpretation],
  latestEvaluationPipeline: (projectId: string, trainingPipelineId?: string) => [
    ...AutoMlQueryKeys.all,
    'latestEvaluationPipeline',
    projectId,
    trainingPipelineId,
  ],
  evaluationPipeline: (projectId: string, pipelineId: string) => [
    ...AutoMlQueryKeys.all,
    'evaluationPipeline',
    projectId,
    pipelineId,
  ],
  evaluationPipelines: (projectId: string) => [...AutoMlQueryKeys.all, 'evaluationPipelines', projectId],
  modelEvaluationPipelines: (projectId: string, modelId?: string) => [
    ...AutoMlQueryKeys.evaluationPipelines(projectId),
    modelId,
  ],
  publishingPipelineStatus: (projectId: string, pipelineId?: string) => [
    ...AutoMlQueryKeys.all,
    'publishingPipelineStatus',
    projectId,
    pipelineId,
  ],
  balancedConfusionMatrix: (projectId: string, pipelineId?: string) => [
    ...AutoMlQueryKeys.all,
    'balancedConfusionMatrix',
    projectId,
    pipelineId,
  ],
  latestPublishingPipeline: (projectId: string) => [...AutoMlQueryKeys.all, 'latestPublishingPipeline', projectId],
  latestSavedFinetune: (projectId: string, modelId?: string) => [
    ...AutoMlQueryKeys.all,
    'latestSavedFinetune',
    projectId,
    modelId,
  ],
  latestTrainingGroups: (projectId: string) => [...AutoMlQueryKeys.all, 'latestTrainingGroups', projectId],
  latestSuccessfulTrainingGroups: (projectId: string) => [
    ...AutoMlQueryKeys.all,
    'latestSuccessfulTrainingGroups',
    projectId,
  ],
  finetuneStatus: (projectId: string, pipelineId?: string) => [
    ...AutoMlQueryKeys.all,
    'finetuneStatus',
    projectId,
    pipelineId,
  ],
  latestImageSelectionPipeline: (projectId: string, modelId?: string) => [
    ...AutoMlQueryKeys.all,
    'latestImageSelectionPipeline',
    projectId,
    modelId,
  ],
  latestImageCleanupPipeline: (projectId: string, modelId?: string) => [
    ...AutoMlQueryKeys.all,
    'latestImageCleanupPipeline',
    projectId,
    modelId,
  ],
  latestImageGenerationPipeline: (projectId: string, modelId?: string) => [
    ...AutoMlQueryKeys.all,
    'latestImageGenerationPipeline',
    projectId,
    modelId,
  ],
  cmImageView: (
    projectId: string,
    pipelineId: string | undefined,
    datasetId: Dataset | undefined,
    interpretation: Interpretation | undefined,
    confidenceThreshold: number | undefined,
    areaSizeThreshold: number,
    sortBy: CMImageSortBy,
    anchor: CMImageAnchor,
    offset: number,
    count: number,
    showMismatchesOnly: boolean,
  ) => [
    ...AutoMlQueryKeys.all,
    'cmImageView',
    projectId,
    pipelineId,
    datasetId,
    interpretation,
    confidenceThreshold,
    areaSizeThreshold,
    sortBy,
    anchor,
    offset,
    count,
    showMismatchesOnly,
  ],
  cmImageViewDistribution: (
    projectId: string,
    pipelineId: string | undefined,
    datasetId: Dataset | undefined,
    interpretation: Interpretation | undefined,
    confidenceThreshold: number | undefined,
    areaSizeThreshold: number,
    bins: number,
    showMismatchesOnly: boolean,
  ) => [
    ...AutoMlQueryKeys.all,
    'cmImageViewDistribution',
    projectId,
    pipelineId,
    datasetId,
    interpretation,
    confidenceThreshold,
    areaSizeThreshold,
    bins,
    showMismatchesOnly,
  ],
};

export function recomputeFinetuneMutationKey(projectId: string) {
  return ['recomputeFinetune', projectId] as const;
}

/** Matches `useDatasetFinetuneDataQuery` keys (includes interpretation). Excludes keypoint queries that reuse the same prefix with `undefined` interpretation. */
export function matchesDatasetFinetuneDataQueryKey(
  queryKey: readonly unknown[],
  params: {projectId: string; pipelineId: string; datasetId: Dataset},
): boolean {
  const interpretation = queryKey[5];
  const isFinetuneInterpretation = interpretation === 'image' || interpretation === 'polygon';

  return (
    queryKey.length >= 6 &&
    isFinetuneInterpretation &&
    queryKey[0] === AutoMlQueryKeys.all[0] &&
    queryKey[1] === 'datasetFinetuneData' &&
    queryKey[2] === params.projectId &&
    queryKey[3] === params.pipelineId &&
    queryKey[4] === params.datasetId
  );
}

export const getFirstPipelineFromTrainingGroup = (trainingGroup?: AutoMLTrainingGroupResponse | null) => {
  if (!trainingGroup || !Array.isArray(trainingGroup.members)) {
    return null;
  }
  return trainingGroup.members[0] ?? null;
};

export function useAllModelPipelinesQuery({enabled = true}: {enabled?: boolean} = {}) {
  const [modelIdParam] = useQueryParam(modelIdQueryParam);
  const modelId = modelIdParam as string | undefined;
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery({
    queryKey: AutoMlQueryKeys.allPipelines(projectId, modelId),
    queryFn: () =>
      getAllModelPipelines({
        modelId,
        projectId,
      }),
    enabled: enabled && !!modelId && typeof modelId === 'string',
    refetchOnWindowFocus: true,
    staleTime: 1000 * 60,
  });
}

export function useLatestSuccessfulModelPipelineQuery({enabled = true}: {enabled?: boolean} = {}) {
  const [modelIdParam] = useQueryParam(modelIdQueryParam);
  const modelId = modelIdParam as string | undefined;
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<AutoMLTrainingGroupResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestSuccessfulPipeline(projectId, modelId),
    queryFn: () =>
      getLatestSuccessfulModelPipeline({
        modelId,
        projectId,
      }),
    enabled: enabled && !!modelId && typeof modelId === 'string',
    refetchOnWindowFocus: true,
    staleTime: 1000 * 30,
  });
}

export function useLatestModelPipelineQuery({refetchInterval = 1000 * 60}: {refetchInterval?: number} = {}) {
  const [modelIdParam] = useQueryParam(modelIdQueryParam);
  const modelId = modelIdParam as string | undefined;
  const [enabled, setEnabled] = useState(true);
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  const query = useQuery<AutoMLTrainingGroupResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestPipeline(projectId, modelId),
    queryFn: async () => {
      queryClient.invalidateQueries({queryKey: AutoMlQueryKeys.allPipelines(projectId, modelId)});
      const data = await getLatestModelPipeline({
        modelId,
        projectId,
      });
      return data;
    },
    enabled: enabled && !!modelId && typeof modelId === 'string',
    refetchOnWindowFocus: true,
    refetchInterval,
    staleTime: 1000 * 10,
  });

  // When the job is done, disable the query to stop polling
  useEffect(() => {
    const doneStates = [JobStates.Failed, JobStates.Success, JobStates.Canceled];
    const jobStatus = getHyperparamAwareJobStatus(query?.data);
    if (jobStatus && doneStates.includes(jobStatus)) {
      setEnabled(false);
    } else {
      setEnabled(true);
    }
  }, [query?.data, query?.data?.members, query?.data?.status]);

  return query;
}

/**
 * Fetches all latest training groups for the project in a single request.
 * This is more efficient than fetching per-model when displaying the models overview.
 * Returns a map of modelId -> trainingGroup for easy lookup.
 */
export function useLatestTrainingGroupsQuery({
  refetchInterval = 1000 * 30,
  enabled: enabledProp = true,
}: {
  refetchInterval?: number;
  enabled?: boolean;
} = {}) {
  const [enabledState, setEnabledState] = useState(true);
  const projectId = useAppSelector(selectCurrentProjectId);

  const query = useQuery<AutoMLTrainingGroupsResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestTrainingGroups(projectId),
    queryFn: async () => {
      const data = await getLatestTrainingGroups({projectId});
      return data;
    },
    enabled: enabledProp && enabledState && !!projectId,
    refetchOnWindowFocus: true,
    refetchInterval,
    staleTime: 1000 * 10,
  });

  // When all jobs are done, disable polling to stop unnecessary requests
  useEffect(() => {
    const doneStates = [JobStates.Failed, JobStates.Success, JobStates.Canceled];
    const trainingGroups = query?.data?.trainingGroups;
    if (!trainingGroups || trainingGroups.length === 0) {
      return;
    }

    // Check if any training group has an active job
    const hasActiveJob = trainingGroups.some((group) => {
      const jobStatus = getHyperparamAwareJobStatus(group);
      return jobStatus && !doneStates.includes(jobStatus);
    });

    setEnabledState(hasActiveJob);
  }, [query?.data?.trainingGroups]);

  return query;
}

export function useLatestSuccessfulTrainingGroupsQuery({enabled = true}: {enabled?: boolean} = {}) {
  const projectId = useAppSelector(selectCurrentProjectId);
  return useQuery<AutoMLTrainingGroupsResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestSuccessfulTrainingGroups(projectId),
    queryFn: () => getLatestSuccessfulTrainingGroups({projectId}),
    enabled: enabled && !!projectId,
  });
}

export function useDatasetFinetuneDataQuery({
  enabled,
  pipelineId,
  datasetId,
  interpretation,
}: {
  enabled: boolean;
  pipelineId?: string;
  datasetId?: Dataset;
  interpretation?: Interpretation;
}) {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectCurrentProjectId);
  const [ranAlready, setRanAlready] = useState(false);
  const currentInterpretation = useRef(interpretation);

  const query = useQuery<FinetuneDataResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.datasetFinetuneData(projectId, pipelineId, datasetId, interpretation),
    queryFn: async () => {
      if (!ranAlready && currentInterpretation.current !== interpretation) {
        dispatch(increaseGlobalLoadingIndicator());
        setRanAlready(true);
        currentInterpretation.current = interpretation;
      }
      try {
        const data = await getDatasetFinetuneDataV2({
          pipelineId: pipelineId!,
          projectId,
          datasetId: datasetId!,
          interpretation,
        });
        return data;
      } finally {
        if (!ranAlready) dispatch(decreaseGlobalLoadingIndicator());
      }
    },
    enabled: enabled && !!pipelineId && !!datasetId,
    staleTime: 0,
    refetchInterval: 1000 * 30,
  });

  return query;
}

export function useBalancedConfusionMatrixQuery({
  enabled,
  pipelineId,
  interpretation,
}: {
  enabled: boolean;
  interpretation: Interpretation;
  pipelineId?: string;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  const query = useQuery<ConfusionData | undefined, HTTPError>({
    queryKey: AutoMlQueryKeys.balancedConfusionMatrix(projectId, pipelineId),
    queryFn: () =>
      getDatasetFinetuneDataV2({
        pipelineId: pipelineId!,
        projectId,
        interpretation,
        datasetId: 'test',
      }).then((data) => data?.confusionData),
    enabled: enabled && !!pipelineId,
  });

  return query;
}

export function useKeypointFinetuneQuery({
  enabled,
  pipelineId,
  datasetId,
}: {
  enabled: boolean;
  pipelineId?: string;
  datasetId?: Dataset;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  const getKeypointFinetune = datasetId ? getKeypointFinetuneDataByDataset : getKeypointFinetuneData;

  const query = useQuery<KeypointFinetuneData | undefined, HTTPError>({
    queryKey: AutoMlQueryKeys.datasetFinetuneData(projectId, pipelineId, datasetId),
    queryFn: () =>
      getKeypointFinetune({
        pipelineId: pipelineId!,
        projectId,
        datasetId,
      }),
    enabled: enabled && !!pipelineId,
  });

  return query;
}

export function useLatestEvaluationPipelineQuery({trainingPipelineId}: {trainingPipelineId?: string}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<AutoMLEvaluationPipelineResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestEvaluationPipeline(projectId, trainingPipelineId),
    queryFn: () =>
      getLatestEvaluationPipelineByTrainingPipeline({
        projectId,
        pipelineId: trainingPipelineId!,
      }).catch((error: unknown) => {
        // A 404 means no evaluation pipeline exists for this training pipeline yet.
        if (error instanceof HTTPError && error.response.status === 404) {
          return null;
        }
        throw error;
      }),
    enabled: !!projectId && !!trainingPipelineId,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 10,
  });
}

export function useLatestSavedFinetuneQuery() {
  const projectId = useAppSelector(selectCurrentProjectId);
  const [modelIdParam] = useQueryParam(modelIdQueryParam);
  const modelId = modelIdParam as string | undefined;

  return useQuery<FinetuneSavedItem | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestSavedFinetune(projectId, modelId),
    queryFn: () =>
      getLatestSavedFinetune({
        projectId,
        modelId,
      }),
    enabled: !!modelId && typeof modelId === 'string',
    retry: (failureCount, error) => error?.response?.status !== 404 && failureCount < 2,
  });
}

export function usePublishingPipelineStatusQuery({
  pipelineId,
}: {
  pipelineId?: string;
  handleSetPublishingPipelineId: (pipelineId: string | undefined) => void;
  isUnpublish?: boolean;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<AutoMLPipelineStatusResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.publishingPipelineStatus(projectId, pipelineId),
    queryFn: () =>
      getPublishingPipelineStatus({
        projectId,
        // if no pipelineId query is disabled
        pipelineId: pipelineId!,
      }),
    enabled: !!pipelineId,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 5,
  });
}

export function useEvaluationPipelineQuery({pipelineId}: {pipelineId: string}) {
  const [enabled, setEnabled] = useState(true);
  const projectId = useAppSelector(selectCurrentProjectId);
  const queryClient = useQueryClient();

  const query = useQuery<AutoMLEvaluationPipelineResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.evaluationPipeline(projectId, pipelineId),
    queryFn: async () => {
      queryClient.invalidateQueries({queryKey: AutoMlQueryKeys.evaluationPipeline(projectId, pipelineId)});
      const data = await getEvaluationPipeline({
        projectId,
        pipelineId,
      });
      return data;
    },
    enabled: enabled,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 10,
  });

  // When the job is done, disable the query to stop polling
  useEffect(() => {
    const doneStates = [JobStates.Failed, JobStates.Success, JobStates.Canceled];
    const jobStatus = query?.data?.statusData?.job?.status;
    if (jobStatus && doneStates.includes(jobStatus)) {
      setEnabled(false);
    } else {
      setEnabled(true);
    }
  }, [query?.data?.statusData?.job?.status]);

  return query;
}

export function useFinetuneStatusQuery({pipelineId}: {pipelineId?: string}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<FinetuneStatusOverviewResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.finetuneStatus(projectId, pipelineId),
    queryFn: () =>
      getFinetuneStatus({
        projectId,
        pipelineId: pipelineId!,
      }),
    enabled: !!pipelineId,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 10,
  });
}

export function useEvaluationPipelinesQuery() {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<AutoMLEvaluationPipelineResponse[] | null, HTTPError>({
    queryKey: AutoMlQueryKeys.evaluationPipelines(projectId),
    queryFn: async () => {
      const data = await getEvaluationPipelines({
        projectId,
      });
      return data;
    },
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 10,
  });
}

export function useModelEvaluationPipelinesQuery({modelId}: {modelId?: string}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<AutoMLEvaluationPipelineResponse[], HTTPError>({
    queryKey: AutoMlQueryKeys.modelEvaluationPipelines(projectId, modelId),
    queryFn: () => getEvaluationPipelinesByModel({projectId, modelId: modelId!}),
    enabled: !!modelId,
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 30,
    staleTime: 1000 * 10,
  });
}

export function useEvaluationPipelineResultsQuery({pipelineId, enabled}: {pipelineId: string; enabled: boolean}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<AutoMLEvaluationPipelineResultsResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.evaluationPipeline(projectId, pipelineId),
    queryFn: async () =>
      getEvaluationPipelineResults({
        projectId,
        pipelineId,
      }),
    enabled,
    staleTime: 1000 * 60,
  });
}

export interface UseLatestEvaluationPipelineQueryOptions {
  enabled?: boolean;
  refetchInterval?: number;
  allModels?: boolean;
}

export function useLatestPublishingPipelineQuery({
  enabled = true,
  refetchInterval = undefined,
  allModels = false,
}: UseLatestEvaluationPipelineQueryOptions) {
  const [modelIdParam] = useQueryParam(modelIdQueryParam);
  const modelId = modelIdParam as string | undefined;
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<AutoMLPublishPipelineResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestPublishingPipeline(projectId),
    queryFn: async () => (await getLatestPublishingPipeline({projectId})) ?? null,
    select: (data) => {
      if (!data || allModels) {
        return data;
      }
      if (!modelId || data.publishModel !== modelId) {
        return null;
      }
      return data;
    },
    enabled: enabled && (allModels || (!!modelId && typeof modelId === 'string')),
    refetchInterval,
    refetchOnWindowFocus: true,
  });
}

export function useLatestImageSelectionPipelineQuery({
  modelId,
  enabled = true,
  refetchInterval,
}: {
  modelId?: string;
  enabled?: boolean;
  refetchInterval?: number;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<GetImageCurationPipelineResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestImageSelectionPipeline(projectId, modelId),
    queryFn: () =>
      getLatestImageSelectionPipeline({
        projectId,
        modelId,
      }),
    enabled: enabled && !!modelId,
    refetchOnWindowFocus: true,
    refetchInterval,
    staleTime: 1000 * 30,
  });
}

export function useLatestImageCleanupPipelineQuery({
  modelId,
  enabled = true,
  refetchInterval,
}: {
  modelId?: string;
  enabled?: boolean;
  refetchInterval?: number;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<GetImageCurationPipelineResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestImageCleanupPipeline(projectId, modelId),
    queryFn: () =>
      getLatestImageCleanupPipeline({
        projectId,
        modelId,
      }),
    enabled: enabled && !!modelId,
    refetchOnWindowFocus: true,
    refetchInterval,
    staleTime: 1000 * 30,
  });
}

export function useLatestSyntheticDefectPipelineQuery({
  modelId,
  enabled = true,
  refetchInterval,
}: {
  modelId?: string;
  enabled?: boolean;
  refetchInterval?: number;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<GetSyntheticDefectPipelineResponse | null, HTTPError>({
    queryKey: AutoMlQueryKeys.latestImageGenerationPipeline(projectId, modelId),
    queryFn: () =>
      getLatestSyntheticDefectPipeline({
        projectId,
        modelId,
      }),
    enabled: enabled && !!modelId,
    refetchOnWindowFocus: true,
    refetchInterval,
    staleTime: 1000 * 30,
  });
}

export function useCmImageViewQuery({
  pipelineId,
  datasetId,
  interpretation,
  confidenceThreshold,
  areaSizeThreshold = 0,
  sortBy,
  anchor,
  offset,
  count,
  showMismatchesOnly,
  enabled = true,
}: {
  pipelineId?: string;
  datasetId?: Dataset;
  interpretation?: Interpretation;
  confidenceThreshold?: number;
  areaSizeThreshold?: number;
  sortBy: CMImageSortBy;
  anchor: CMImageAnchor;
  offset: number;
  count: number;
  showMismatchesOnly: boolean;
  enabled?: boolean;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<CMImageViewResponse, HTTPError>({
    queryKey: AutoMlQueryKeys.cmImageView(
      projectId,
      pipelineId,
      datasetId,
      interpretation,
      confidenceThreshold,
      areaSizeThreshold,
      sortBy,
      anchor,
      offset,
      count,
      showMismatchesOnly,
    ),
    queryFn: ({signal}) =>
      getCmImageView({
        projectId,
        pipelineId: pipelineId!,
        datasetId: datasetId!,
        interpretation: interpretation!,
        confidenceThreshold: confidenceThreshold!,
        areaSizeThreshold,
        sortBy,
        anchor,
        offset,
        count,
        showMismatchesOnly,
        reactQuerySignal: signal,
      }),
    enabled:
      enabled && !!projectId && !!pipelineId && !!datasetId && !!interpretation && confidenceThreshold !== undefined,
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
  });
}

export function useCmImageViewWindowsQuery({
  pipelineId,
  datasetId,
  interpretation,
  confidenceThreshold,
  areaSizeThreshold = 0,
  sortBy,
  count,
  showMismatchesOnly,
  windowOffsets,
  enabled = true,
}: {
  pipelineId?: string;
  datasetId?: Dataset;
  interpretation?: Interpretation;
  confidenceThreshold?: number;
  areaSizeThreshold?: number;
  sortBy: CMImageSortBy;
  count: number;
  showMismatchesOnly: boolean;
  windowOffsets: number[];
  enabled?: boolean;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);
  const ready =
    enabled && !!projectId && !!pipelineId && !!datasetId && !!interpretation && confidenceThreshold !== undefined;

  return useQueries({
    queries: windowOffsets.map((offset) => ({
      queryKey: AutoMlQueryKeys.cmImageView(
        projectId,
        pipelineId,
        datasetId,
        interpretation,
        confidenceThreshold,
        areaSizeThreshold,
        sortBy,
        'offset' as CMImageAnchor,
        offset,
        count,
        showMismatchesOnly,
      ),
      queryFn: ({signal}: {signal: AbortSignal}) =>
        getCmImageView({
          projectId,
          pipelineId: pipelineId!,
          datasetId: datasetId!,
          interpretation: interpretation!,
          confidenceThreshold: confidenceThreshold!,
          areaSizeThreshold,
          sortBy,
          anchor: 'offset',
          offset,
          count,
          showMismatchesOnly,
          reactQuerySignal: signal,
        }),
      enabled: ready,
      placeholderData: (previousData: CMImageViewResponse | undefined) => previousData,
      staleTime: 1000 * 30,
    })),
  });
}

export function useCmImageViewDistributionQuery({
  pipelineId,
  datasetId,
  interpretation,
  confidenceThreshold,
  areaSizeThreshold = 0,
  bins,
  showMismatchesOnly = false,
  enabled = true,
}: {
  pipelineId?: string;
  datasetId?: Dataset;
  interpretation?: Interpretation;
  confidenceThreshold?: number;
  areaSizeThreshold?: number;
  bins: number;
  showMismatchesOnly?: boolean;
  enabled?: boolean;
}) {
  const projectId = useAppSelector(selectCurrentProjectId);

  return useQuery<CMImageDistributionResponse, HTTPError>({
    queryKey: AutoMlQueryKeys.cmImageViewDistribution(
      projectId,
      pipelineId,
      datasetId,
      interpretation,
      confidenceThreshold,
      areaSizeThreshold,
      bins,
      showMismatchesOnly,
    ),
    queryFn: ({signal}) =>
      getCmImageViewDistribution({
        projectId,
        pipelineId: pipelineId!,
        datasetId: datasetId!,
        interpretation: interpretation!,
        confidenceThreshold: confidenceThreshold!,
        areaSizeThreshold,
        bins,
        showMismatchesOnly,
        reactQuerySignal: signal,
      }),
    enabled:
      enabled && !!projectId && !!pipelineId && !!datasetId && !!interpretation && confidenceThreshold !== undefined,
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
  });
}
